import React from "react";
import { CheckCircle2, Users, Car as CarIcon, AlertTriangle, Layers } from "lucide-react";

/**
 * SidebarSummary — right column on Planificator (week resource overview).
 * Extracted from Planificator.jsx (was lines ~1407-1483).
 *
 * Props (all from parent state):
 *  - freePersons, busyPersons, onLeavePersons: array of person summaries
 *  - freeCars: array of car summaries
 */
export default function SidebarSummary({ freePersons = [], busyPersons = [], onLeavePersons = [], freeCars = [] }) {
  return (
    <div className="space-y-4">
      <div className="card-flat p-4">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="text-green-600" size={18}/>
          <h3 className="font-display font-bold text-sm">Disponibili săptămâna aceasta</h3>
        </div>
        <div className="text-xs text-zinc-500 mb-2">
          <strong className="text-green-700">{freePersons.length}</strong> persoane ·{" "}
          <strong className="text-green-700">{freeCars.length}</strong> autoturisme
        </div>
        <div className="space-y-1 max-h-[150px] overflow-y-auto">
          {freePersons.slice(0, 8).map((p) => (
            <div key={p.id} className="text-xs px-2 py-1 rounded bg-green-50 border border-green-100 flex items-center gap-1.5">
              <Users size={11} className="text-green-600"/> <span className="flex-1 truncate">{p.name}</span>
            </div>
          ))}
          {freePersons.length > 8 && <div className="text-[10px] text-zinc-400 px-2">+ alte {freePersons.length - 8}</div>}
          {onLeavePersons.length > 0 && (
            <div className="pt-2 mt-2 border-t border-green-100">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mb-1 px-1">În concediu / indisponibili</div>
              {onLeavePersons.slice(0, 5).map((p) => {
                const v = (p.vacations || [])[0] || {};
                const isUnavail = v.kind === "unavailable";
                return (
                  <div key={p.id} className={`text-xs px-2 py-1 rounded border flex items-center gap-1.5 ${isUnavail ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200"}`}>
                    <span className="text-[11px]">{isUnavail ? "🚫" : "🏖"}</span>
                    <span className="flex-1 truncate">{p.name}</span>
                    <span className={`text-[9px] font-bold uppercase ${isUnavail ? "text-amber-700" : "text-blue-700"}`}>
                      {isUnavail ? "Indisp." : "Concediu"}
                    </span>
                  </div>
                );
              })}
              {onLeavePersons.length > 5 && <div className="text-[10px] text-zinc-400 px-2">+ alți {onLeavePersons.length - 5}</div>}
            </div>
          )}
        </div>
        <div className="space-y-1 mt-2 max-h-[150px] overflow-y-auto">
          {freeCars.slice(0, 6).map((c) => (
            <div key={c.id} className="text-xs px-2 py-1 rounded bg-green-50 border border-green-100 flex items-center gap-1.5">
              <CarIcon size={11} className="text-green-600"/> {c.label}
            </div>
          ))}
          {freeCars.length > 6 && <div className="text-[10px] text-zinc-400 px-2">+ alte {freeCars.length - 6}</div>}
        </div>
      </div>

      <div className="card-flat p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="text-amber-600" size={18}/>
          <h3 className="font-display font-bold text-sm">Alocați deja</h3>
        </div>
        <div className="text-xs text-zinc-500 mb-2">
          <strong className="text-amber-700">{busyPersons.length}</strong> persoane alocate în această săptămână
        </div>
        <div className="space-y-1 max-h-[220px] overflow-y-auto">
          {busyPersons.slice(0, 10).map((p) => (
            <div key={p.id} className="text-xs px-2 py-1.5 rounded bg-amber-50 border border-amber-100">
              <div className="font-medium">{p.name}</div>
              <div className="text-[10px] text-zinc-500 truncate">
                {(p.assignments || []).map((a) => a.label).join(" · ")}
              </div>
            </div>
          ))}
          {busyPersons.length === 0 && <div className="text-[11px] text-zinc-400 italic px-2">Nimeni alocat.</div>}
        </div>
      </div>

      <div className="card-flat p-4">
        <div className="flex items-center gap-2 mb-2">
          <Layers size={18} className="text-[var(--primary)]"/>
          <h3 className="font-display font-bold text-sm">Legendă</h3>
        </div>
        <p className="text-[11px] text-zinc-500">Click pe o intrare din calendar pentru a aloca persoane și autoturisme. Conflictele sunt marcate automat.</p>
      </div>
    </div>
  );
}
